"use client";

import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        <div className="h-6 w-20 animate-pulse rounded-md bg-muted" />
        <div className="h-6 w-20 animate-pulse rounded-md bg-muted" />
        <div className="h-6 w-20 animate-pulse rounded-md bg-muted" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative inline-flex items-center justify-center px-2 py-2 font-sans text-xs font-medium uppercase tracking-widest text-foreground transition-colors hover:text-primary focus:text-primary focus:outline-none">
                {item.label}
                <span className="absolute bottom-0 left-1/2 h-[1px] w-0 bg-primary transition-all duration-300 ease-out group-hover:left-0 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  if (loading) {
    return <div className="h-9 w-9 animate-pulse rounded-md bg-muted md:hidden" />;
  }
  return <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:text-primary hover:bg-transparent">
          <Menu className="size-6" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[300px] bg-background border-l border-border sm:w-[400px]">
        <div className="flex flex-col gap-8 pt-12">
          <div className="flex justify-center">
            <Link to="/" onClick={() => setIsOpen(false)}>
              <WvcLogo className="h-8 w-auto text-foreground" />
            </Link>
          </div>
          <nav className="flex flex-col gap-4">
            {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} onClick={() => setIsOpen(false)} data-index={i} className="group relative inline-flex w-fit items-center justify-start px-2 py-2 font-sans text-lg font-medium uppercase tracking-widest text-foreground transition-colors hover:text-primary">
                {item.label}
                <span className="absolute bottom-0 left-0 h-[1px] w-full origin-bottom-right scale-x-0 bg-primary transition-transform duration-300 ease-out group-hover:origin-bottom-left group-hover:scale-x-100" />
              </Link>)}
          </nav>
        </div>
      </SheetContent>
    </Sheet>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="264700" id="header" className={cn("sticky top-0 z-50 w-full bg-background text-foreground transition-all duration-500", isScrolled && "bg-background/80 backdrop-blur-md border-b border-border/30")}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex h-20 items-center justify-between">
          {}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center">
              <WvcLogo className="h-6 w-auto text-foreground font-default" />
            </Link>
          </div>

          {}
          <MenuProvider menu_id="16">
            <DesktopMenuConsumer />
          </MenuProvider>

          {}
          <MenuProvider menu_id="16">
            <MobileMenuConsumer />
          </MenuProvider>
        </div>
      </div>
    </section>;
}